
import { resetUnsavedInvoiceDetails, setInvoiceDetails, setSavedInvoiceDetails, useInvoiceDetails } from '../../../../state/billing/invoice-details.atom';
import React, { useEffect } from 'react';
import { Row, Subtitle } from '../domElements/billing';
import { useTranslation } from 'react-i18next';
import { Component, InputText, SubTitle } from '../../browser-template/styles';
import { InvoiceDetails, ValidateInvoiceDetailsResult, ValidateInvoiceDetailsStatus } from '../types/invoice-details.types';
import { submitNewInvoiceDetails } from '../../overview/api';
import { Button, message } from 'antd';

const InvoiceInput: React.FC = (): JSX.Element => {
  const { companyName = '', address = '', taxId = '' } = useInvoiceDetails() || {};

  const { t: translation } = useTranslation();

  useEffect(() => {
    return () => {
      resetUnsavedInvoiceDetails();
    };
  }, []);

  const onUpdateInvoiceDetails = (field: keyof InvoiceDetails, event: React.ChangeEvent<HTMLInputElement>): void => {
    const invoiceDetailsToUpdate = {
      companyName, address, taxId,
    };

    invoiceDetailsToUpdate[field] = event.target.value;
    setInvoiceDetails(invoiceDetailsToUpdate);
  };

  const handleSubmitInvoiceDetails = async (): Promise<void> => {
    const invoiceDetails = {
      companyName,
      address,
      taxId,
    }

    const validateResult = validateInvoiceDetails(invoiceDetails);
    const { status } = validateResult;
    if (status === ValidateInvoiceDetailsStatus.ERROR) {
      const { message: errorMessage } = validateResult;
      message.error(errorMessage);

      return;
    }

    await submitNewInvoiceDetails(invoiceDetails);
    setSavedInvoiceDetails(invoiceDetails);
    message.success(translation('personalArea.billing.alertInvoiceDetailsSaved'));
  };

  
  const validateInvoiceDetails = (invoiceDetails: InvoiceDetails): ValidateInvoiceDetailsResult  => {
    const allowedCharsRegex = /^[\x20-\x7E]+$/;
  
    const notEmpty = translation('personalArea.billing.invoiceValidateNotEmpty').toString();
    const onlyLatin = translation('personalArea.billing.invoiceValidateLatinCharacters').toString();
    const maximumLength = translation('personalArea.billing.invoiceValidateMaxLength').toString();
    const keys = Object.keys(invoiceDetails) as Array<keyof InvoiceDetails>;
    const fieldNamesTranslations = {
      companyName: translation('personalArea.billing.incoiceFullName').toString(),
      address: translation('personalArea.billing.invoiceAddress').toString(),
      taxId: translation('personalArea.billing.invoiceTaxId').toString(),
    };

    for (const key of keys) {
      const field = invoiceDetails[key];
      if (field?.length && field.length > 150) {
        const fieldName = fieldNamesTranslations[key];
      
        return { status: ValidateInvoiceDetailsStatus.ERROR, message: `${fieldName} ${maximumLength}` };
      }
    }

    if (!invoiceDetails.companyName.trim()) {
      const fieldName = translation('personalArea.billing.incoiceFullName').toString();
      
      return { status: ValidateInvoiceDetailsStatus.ERROR, message: `${fieldName} ${notEmpty}` };
    }

    if (!allowedCharsRegex.test(invoiceDetails.companyName)) {
      const fieldName = translation('personalArea.billing.incoiceFullName').toString();
      
      return { status: ValidateInvoiceDetailsStatus.ERROR, message: `${fieldName} ${onlyLatin}` };
    }
  
    if (!invoiceDetails.address.trim()) {
      const fieldName = translation('personalArea.billing.invoiceAddress').toString();
      return { status: ValidateInvoiceDetailsStatus.ERROR, message: `${fieldName} ${notEmpty}` };
    }

    if (!allowedCharsRegex.test(invoiceDetails.address)) {
      const fieldName = translation('personalArea.billing.invoiceAddress').toString();
      return { status: ValidateInvoiceDetailsStatus.ERROR, message: `${fieldName} ${onlyLatin}` };
    }
  
    if (invoiceDetails.taxId && !allowedCharsRegex.test(invoiceDetails.taxId)) {
      const fieldName = translation('personalArea.billing.invoiceTaxId').toString();
      return { status: ValidateInvoiceDetailsStatus.ERROR, message: `${fieldName} ${onlyLatin}` };
    }
  
    return { status: ValidateInvoiceDetailsStatus.OK };
  }

  return (
    <>
      <Subtitle>
        {translation('personalArea.billing.subTitleInvoiceDetails')}
      </Subtitle>
      <Row>
        <Component>
          <SubTitle>
            {translation('personalArea.billing.incoiceFullName')}
          </SubTitle>
          <Row>
            <InputText
              placeholder={translation('personalArea.billing.incoiceFullName') || ''}
              value={companyName}
              onChange={(event) => onUpdateInvoiceDetails('companyName', event)}
              onBlur={(): void => void 0}
            />
          </Row>
        </Component>
      </Row>
      <Row>
        <Component>
          <SubTitle>
            {translation('personalArea.billing.invoiceAddress')}
          </SubTitle>
          <Row>
            <InputText
              placeholder={translation('personalArea.billing.invoiceAddress') || ''}
              value={address}
              onChange={(event) => onUpdateInvoiceDetails('address', event)}
              onBlur={(): void => void 0}
            />
          </Row>
        </Component>
      </Row>
      <Row>
        <Component>
          <SubTitle>
            {translation('personalArea.billing.invoiceTaxId')}
          </SubTitle>
          <Row>
            <InputText
              placeholder={translation('personalArea.billing.invoiceTaxId') || ''}
              value={taxId}
              onChange={(event) => onUpdateInvoiceDetails('taxId', event)}
              onBlur={(): void => void 0}
            />
          </Row>
        </Component>
      </Row>
      <Row>
        <Component>
          <Button
            disabled={false}
            onClick={handleSubmitInvoiceDetails}
            type='default'
            style={{ backgroundColor: 'var(--F7F7F8-personal-area-billing)' }}
          >
            {translation('personalArea.billing.applySubmitBtnText')}
          </Button>
        </Component>
      </Row>
    </>
  );
}

export default InvoiceInput;
